import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Dropdown from "react-bootstrap/Dropdown";
import { UiComponents, UnoStoreHooks } from "@simulanisindia/ui";
import apiclient from "../../apiclient";
import { USER_LIMIT } from "../../constant/constant";
import { GROUP_CHILDREN_USERS, PROJECT_SHARE } from "../../apiclient/apiEndpoints";
import {
  getPlaceholderName,
  removeDuplicatesByKey,
  removeMatchedDataUsingKey,
  toggleArrayByObject,
} from "../../utils/general";
import { GROUP_TAB_TYPE, USER_TAB_TYPE } from "../../utils/constant";
import useDebounce from "../../hooks/useDebounce";
import BtnLoad from "../common/BtnLoad";
import { NoUserFound, UserLoadItem } from "./ShareModal";
import { CheckIcon } from "../../assets/icons/icons";

const { SimCheckBox, SearchBar, TabNavigator, SimAccordion } = UiComponents;
const { useUnoSelector } = UnoStoreHooks;

const navTabData = [
  { id: 1, title: "Users", key: USER_TAB_TYPE },
  { id: 2, title: "Groups", key: GROUP_TAB_TYPE },
];

const roles = [
  { key: "viewer", label: "Read" },
  { key: "editor", label: "Edit" }
];

const ShareEditModal = ({ onHide, details, handleOpenShareModal }) => {
  const configData = useUnoSelector((state) => state.config);
  const userData = useUnoSelector((state) => state.user);
  let groupId = userData.user.group.uuid;
  const [activeTab, setActiveTab] = useState(USER_TAB_TYPE);
  const groupData = useSelector((state) => state.groupSlice);
  const [search, setSearch] = useState("");
  const [defaultRole, setDefaultRole] = useState("viewer");
  const [loading, setLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [groupHierarchy, setAllGroupsHierarchy] = useState({
    list: [],
    hierarchy: [],
  });

  const [selectedUserGroup, setSelectedUserGroup] = useState({
    [USER_TAB_TYPE]: [],
    [GROUP_TAB_TYPE]: [],
  });

  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    setAllGroupsHierarchy({
      list: groupData?.allChildren || [],
      hierarchy: groupData?.allHierarchy ? [groupData?.allHierarchy] : [],
    });
  }, [details, groupData]);

  const getUsersList = async () => {
    setLoading(true);
    try {
      let data = await apiclient.get(`${configData.config.UNO_URL}${GROUP_CHILDREN_USERS}/${groupId}`, {
        params: {
          limit: USER_LIMIT,
          page: 1,
          search: search,
        },
      });
      let filterList = data.filter((e) => e.uuid !== userData.user.uuid);
      setUsersList(filterList);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUsersList();
  }, [details, debouncedSearch]);

  const handleAssignProject = async () => {
    let _projectId = details?.uuid || null;
    if (assignLoading || !_projectId) return;
    try {
      setAssignLoading(true);
      let allUsers = selectedUserGroup[USER_TAB_TYPE].map((e) => ({
        categoryType: "user",
        id: e.id,
        type: defaultRole,
      }));
      let allGroups = selectedUserGroup[GROUP_TAB_TYPE].map((e) => ({
        categoryType: "group",
        id: e.id,
        type: defaultRole,
      }));

      let bodyData = {
        data: [...allUsers, ...allGroups],
      };
      await apiclient.post(`${configData.config.API_URL}${PROJECT_SHARE}/${_projectId}`, bodyData);
      if (handleOpenShareModal) handleOpenShareModal();
    } catch (error) {
      console.log(error);
    } finally {
      setAssignLoading(false);
    }
  };

  const handleCheckboxChange = (data, type) => {
    const { name, uuid: id, avatar, email, uid } = data;
    const newData = type === USER_TAB_TYPE ? { name, id, uid, avatar, email } : { name, id, avatar };
    setSelectedUserGroup((pre) => ({
      ...pre,
      [type]: toggleArrayByObject(pre[type], newData),
    }));
  };

  const handleSelectAllGroupChildren = ({ group }) => {
    let currentGroup = { name: group.name, id: group.id, avatar: group.avatar };
    let selectedChildren = group.children.map((e) => ({ name: e.name, id: e.id, avatar: e.avatar }));
    let allGroups = [...selectedChildren, ...selectedUserGroup[GROUP_TAB_TYPE], currentGroup];
    let uniqueGroups = removeDuplicatesByKey(allGroups, "id");
    setSelectedUserGroup((pre) => ({
      ...pre,
      [GROUP_TAB_TYPE]: uniqueGroups,
    }));
  };

  const handleUnSelectAllGroupChildren = ({ group }) => {
    let currentGroup = { name: group.name, id: group.id, avatar: group.avatar };
    let selectedChildren = group.children.map((e) => ({ name: e.name, id: e.id, avatar: e.avatar }));
    let allGroups = [...selectedChildren, currentGroup];
    let uniqueGroups = removeDuplicatesByKey(allGroups, "id");
    let alreadySelectedGroup = selectedUserGroup[GROUP_TAB_TYPE];

    setSelectedUserGroup((pre) => ({
      ...pre,
      [GROUP_TAB_TYPE]: removeMatchedDataUsingKey(alreadySelectedGroup, uniqueGroups, "id"),
    }));
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case USER_TAB_TYPE:
        return (
          <RenderUserList
            selectedUserGroup={selectedUserGroup}
            handleCheckboxChange={handleCheckboxChange}
            usersList={usersList}
            loading={loading}
            debouncedSearch={debouncedSearch}
          />
        );
      case GROUP_TAB_TYPE:
        return (
          <GroupList
            search={search}
            selectedUserGroup={selectedUserGroup}
            handleCheckboxChange={handleCheckboxChange}
            groupHierarchy={groupHierarchy}
            handleSelectAllGroupChildren={handleSelectAllGroupChildren}
            handleUnSelectAllGroupChildren={handleUnSelectAllGroupChildren}
            loading={loading}
            debouncedSearch={debouncedSearch}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="assignmodal share-modal grouphirarchy">
        <div className="modal-head">
          <h6>Share "{details?.name || ""}"</h6>
          <div className="search-parent">
            <SearchBar placeholder="Search ..." value={search} onChange={(e) => setSearch(e.target.value)} />
            <div className="dropdown-here">
              <Dropdown>
                <Dropdown.Toggle > {roles.find(r => r.key === defaultRole)?.label} </Dropdown.Toggle>
                <Dropdown.Menu>
                  {roles.map(({ key, label }) => (
                    <Dropdown.Item key={key} onClick={() => setDefaultRole(key)}>
                      <span className="option">
                        {defaultRole === key && (
                          <span className="checked">
                            <CheckIcon />
                          </span>
                        )}
                        <span>{label}</span>
                      </span>
                    </Dropdown.Item>
                  ))}

                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>
        <div className="modal-body">
          <TabNavigator
            tabs={navTabData}
            defaultActiveKey={activeTab}
            nav_wrapper_class="with-search"
            setActiveTab={setActiveTab}
          />

          {renderTabContent()}

          <div className="modal-footer share-footer justify-content-end">
            <div className="modal-footer-btns gap-2">
              <button className="cancel" onClick={onHide}>
                Cancel
              </button>
              <button className="save btn-save" onClick={handleAssignProject}>
                {assignLoading ? <BtnLoad /> : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShareEditModal;

const RenderUserList = ({ usersList, selectedUserGroup, handleCheckboxChange, loading, debouncedSearch }) => {
  return (
    <div className="user-list mt-0">
      {loading ? (
        <UserLoadItem />
      ) : usersList.length === 0 ? (
        <NoUserFound searchValue={debouncedSearch} editShareModal="users" />
      ) : (
        usersList.map((user) => (
          <div className="user-item ps-0">
            <div className="user-info" key={user.uuid}>
              <SimCheckBox
                checked={selectedUserGroup[USER_TAB_TYPE].some((e) => e.id === user.uuid)}
                onChange={() => handleCheckboxChange(user, USER_TAB_TYPE)}
              />
              <UserAvatar user={user} />
              <div className="name-email">
                <p className="name m-0">{user.name}</p>
                <p className="email m-0">{user.email || user.uid}</p>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export const GroupList = ({
  search,
  selectedUserGroup,
  handleCheckboxChange,
  groupHierarchy,
  handleSelectAllGroupChildren,
  handleUnSelectAllGroupChildren,
  loading,
  debouncedSearch,
}) => {
  let groupHierarchyList = groupHierarchy?.list?.filter((e) => e.name.toLowerCase().includes(search?.toLowerCase()));

  return (
    <>
      <div className="user-list mt-0">
        {search.length > 0 ? (
          loading ? (
            <UserLoadItem />
          ) : groupHierarchyList.length === 0 ? (
            <NoUserFound searchValue={debouncedSearch} editShareModal="groups" />
          ) : (
            <div className="user-list mt-0">
              {groupHierarchyList.map((group) => {
                return (
                  <div className="user-item" key={group.uuid}>
                    <div className="user-info  ">
                      <SimCheckBox
                        id={`checkbox-${group.uuid}`}
                        checked={selectedUserGroup[GROUP_TAB_TYPE].some((e) => e.id === group.uuid)}
                        onChange={() => handleCheckboxChange(group, GROUP_TAB_TYPE)}
                      />
                      <UserAvatar user={group} />
                      <div className="name-email">
                        <p className="name m-0">{group.name}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <>
            {renderGroups(
              groupHierarchy.hierarchy,
              selectedUserGroup,
              handleCheckboxChange,
              handleSelectAllGroupChildren,
              handleUnSelectAllGroupChildren
            )}
          </>
        )}
      </div>
    </>
  );
};

function checkListIdsInSelected(selected, list) {
  return list.every((listItem) => selected.some((selectedItem) => selectedItem.id === listItem.id));
}

export const renderGroups = (
  groupList,
  selectedUserGroup,
  handleCheckboxChange,
  handleSelectAllGroupChildren,
  handleUnSelectAllGroupChildren
) => {
  return groupList.map((group) => {
    let allChildren = group?.children.map((e) => ({ id: e.id }));
    let haveChildren = allChildren.length > 0 ? true : false;
    return (
      <SimAccordion
        key={group.id}
        checkbox={
          <SimCheckBox
            checked={selectedUserGroup[GROUP_TAB_TYPE].some((e) => e.id === group.id)}
            onChange={() =>
              handleCheckboxChange({ name: group.name, uuid: group.id, avatar: group.avatar }, GROUP_TAB_TYPE)
            }
          />
        }
        button={
          haveChildren ? (
            <>
              {checkListIdsInSelected(selectedUserGroup[GROUP_TAB_TYPE], [...allChildren, { id: group.id }]) ? (
                <button className="roundednormalbtn" onClick={() => handleUnSelectAllGroupChildren({ group })}>
                  Unselect all
                </button>
              ) : (
                <button className="roundednormalbtn" onClick={() => handleSelectAllGroupChildren({ group })}>
                  Select all
                </button>
              )}
            </>
          ) : null
        }
        avatar={group.avatar}
        haveMoreDropdown={haveChildren}
      >
        <div heading={group.name}>
          {group.children &&
            renderGroups(
              group.children,
              selectedUserGroup,
              handleCheckboxChange,
              handleSelectAllGroupChildren,
              handleUnSelectAllGroupChildren
            )}
        </div>
      </SimAccordion>
    );
  });
};

export const UserAvatar = ({ user }) => {
  const [imageError, setImageError] = useState(false);
  return (
    <>
      {!imageError && user.avatar ? (
        <div className="thumnail ">
          <img
            className="smallavatar"
            src={user.avatar}
            alt={user.name}
            onError={() => setImageError(true)}
            referrerPolicy="no-referrer"
          />
        </div>
      ) : (
        <span className="init">{getPlaceholderName(user.name)}</span>
      )}
    </>
  );
};
